import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  createStyles,
  Grid,
  Badge,
  makeStyles,
  Tabs as MuiTabs,
  Tab as MuiTab,
  Theme,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';

import { Tab as TabLink } from '../../services/TabLinkService/types';
import hotKeyConverter from '../../utility/keyCodeConverter';

const useStyles = makeStyles(({ breakpoints, palette, colors }: Theme) => createStyles({
  tabsContainer: {
    flexGrow: 1,
  },

  tabsRoot: {
    minHeight: '35px',

    [breakpoints.down('md')]: {
      '& div:first-child': {
        width: '0',
      },
      '& div.MuiButtonBase-root': {
        width: '30px',
      },
    },
  },
  tabsFlexContainer: {},
  tabsScrollButtons: {
    width: 30,
  },
  tabsIndicator: {
    display: 'none',
  },

  tabRoot: {
    minWidth: 55,
    minHeight: '35px',
    textTransform: 'capitalize',
    color: colors.tabs,
    opacity: 1,
    '&:focus': {
      outline: 'none',
    },
    '&:hover': {
      color: palette.tabs.hover,
    },
  },
  tabSelected: {
    borderRadius: '5px',
    backgroundColor: palette.tabs.selected,
  },
  tabWrapper: {
    alignItems: 'flex-start',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
    fontSize: 14,
  },
  badge: {
    top: '0px',
    right: '-5px',
    height: '11px',
    minWidth: '9px',
    fontSize: '9px',
  },

  tabContentRoot: {
    width: '100%',
    marginTop: '1em',
  },
}), { name: 'Tabs' });

export type TabComponentProps = {
  active: boolean;
};

interface TabBadge {
  variant?: 'primary' | 'secondary' | 'default' | 'error';
  value: React.ReactText;
}

const noBadge: TabBadge = {
  variant: 'default',
  value: 0,
};

const pager = (min: number, max: number) => ({
  next: (state: number) => (state + 1 > max ? min : state + 1),
  prev: (state: number) => (state - 1 < min ? max : state - 1),
});

export interface Tab<P> {
  label: string;
  component: React.ComponentType<P & TabComponentProps>;
  badge?: TabBadge;
}

type Props<P> = {
  tabs: Array<Tab<P>>;
  tabProps: P;
  tabLinks: TabLink[];
  currentTab?: number;
  onChangeTab?: (indexTab: number) => void;
};

const Tabs = <P extends object>({
  tabs,
  tabProps,
  tabLinks,
  currentTab = 0,
  onChangeTab,
}: Props<P>): React.ReactElement | null => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tab, setTab] = useState(currentTab);

  useEffect(() => {
    setTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    if (onChangeTab !== undefined) {
      onChangeTab(tab);
      if (currentTab !== tab) {
        dispatch(push(tabLinks[tab].to));
      }
    }
  }, [tab, onChangeTab, dispatch, tabLinks, currentTab]);

  const handleTabChange = useCallback((event, newValue) => {
    setTab(newValue);
    dispatch(push(tabLinks[newValue].to));
  }, [dispatch, setTab, tabLinks]);

  const { prev, next } = useMemo(() => pager(0, tabs.length - 1), [tabs.length]);

  useEffect(() => {
    const hotKeyHandler = (event: KeyboardEvent) => {
      if (
        event.ctrlKey && event.keyCode === hotKeyConverter('bracketLeft')
      ){
        event.preventDefault();
        setTab(prev);
      }
      if (
        event.ctrlKey && event.keyCode === hotKeyConverter('bracketRight')
      ){
        event.preventDefault();
        setTab(next);
      }
    };
    window.addEventListener('keydown', hotKeyHandler);

    return () => {
      window.removeEventListener('keydown', hotKeyHandler);
    }
  }, [setTab, prev, next]);

  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Grid container spacing={3}>
      <Grid item lg={2} xs={12} className={classes.tabsContainer}>
        <MuiTabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          orientation={compact ? 'horizontal' : 'vertical'}
          variant="scrollable"
          scrollButtons={compact ? 'on' : 'auto'}
          classes={{
            flexContainer: classes.tabsFlexContainer,
            scrollButtons: classes.tabsScrollButtons,
            root: classes.tabsRoot,
            indicator: classes.tabsIndicator,
          }}
        >
          {tabs.map(
            ({ label, badge: { variant, value } = noBadge, component }) => (
              <MuiTab
                tabIndex={-1}
                label={(
                  <Badge
                    badgeContent={value}
                    color={variant}
                    classes={{
                      badge: classes.badge,
                    }}
                  >
                    {label}
                  </Badge>
                )}
                classes={{
                  root: classes.tabRoot,
                  selected: classes.tabSelected,
                  wrapper: classes.tabWrapper,
                }}
                key={`${label}_${component.displayName}`}
              />
            ),
          )}
        </MuiTabs>
      </Grid>
      <Grid item lg={10} xs={12}>
        {tabs.map(({ component: TabComponent, label }, index) => (
          <div
            role="tabpanel"
            hidden={tab !== index}
            id={`tabpanel-${label.toLowerCase()}`}
            className={classes.tabContentRoot}
            key={`${label}_${TabComponent.displayName}`}
          >
            <TabComponent {...tabProps} active={tab === index} />
          </div>
        ))}
      </Grid>
    </Grid>
  );
};

export default Tabs;
